import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Download, ClipboardList, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Self-contained panel for the Run Sheet (General Officials Sheet).
 *
 * Single competition selector, gated on the Skating Order draw existing.
 * One-click generate → downloads .xlsx. All content is sourced from the
 * database; styling follows the active document colour scheme.
 */
export default function RunSheetPanel({ competitionsWithOOS = [], competitionId = '' }) {
  const compId = competitionId;
  const [preflight, setPreflight] = useState(null);
  const [loadingPreflight, setLoadingPreflight] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!compId) {
      setPreflight(null);
      return;
    }
    setLoadingPreflight(true);
    axios
      .get(`${API}/documents/general-officials-sheet/${compId}/preflight`, { withCredentials: true })
      .then((res) => setPreflight(res.data))
      .catch((err) => {
        toast.error(err.response?.data?.detail || 'Failed to load competition status');
        setPreflight(null);
      })
      .finally(() => setLoadingPreflight(false));
  }, [compId]);

  const competition = competitionsWithOOS.find((c) => c.competition_id === compId);

  const handleGenerate = async () => {
    if (!compId) {
      toast.error('Select a competition first');
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/documents/general-officials-sheet?competition_id=${compId}`,
        {},
        { withCredentials: true, responseType: 'blob' }
      );
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : 'Officials_Sheet.xlsx';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Run Sheet generated');
    } catch (err) {
      let msg = 'Failed to generate Run Sheet';
      if (err.response?.data instanceof Blob) {
        try {
          const txt = await err.response.data.text();
          const json = JSON.parse(txt);
          msg = json.detail || msg;
        } catch { /* keep default */ }
      } else if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const ready = !!preflight && preflight.has_oos && preflight.has_draw;

  return (
    <Card className="p-6 space-y-5" data-testid="run-sheet-panel">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <ClipboardList className="h-5 w-5 text-[#2D4A3E]" />
          <h3 className="text-lg font-semibold text-[#2D4A3E]">Run Sheet</h3>
        </div>
        <p className="text-sm text-stone-600">
          Generate the General Officials Sheet (run sheet) — one row per skater, in skating order, per event.
          Average Mark Calcs tables are included for non-RollArt events. Styling follows the active document colour scheme.
        </p>
      </div>

      <div>
        <p className="text-sm text-stone-600">
          {!compId ? (
            <>Pick a competition above to continue.</>
          ) : (() => {
            const inOOS = competitionsWithOOS.find((c) => c.competition_id === compId);
            if (!inOOS) {
              return <span className="text-amber-900">This competition doesn't have an Order of Skate yet.</span>;
            }
            return <>Generating Run Sheet for <span className="font-medium">{inOOS.name}</span>.</>;
          })()}
        </p>
      </div>

      {compId && loadingPreflight && (
        <div className="text-sm text-stone-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking competition status…
        </div>
      )}

      {compId && preflight && !loadingPreflight && (
        <div className="rounded-lg border bg-stone-50 px-3 py-3 text-sm">
          {ready ? (
            <div className="space-y-1">
              <p>
                <span className="font-medium">{preflight.n_events}</span> events,{' '}
                <span className="font-medium">{preflight.n_skater_records}</span> skater records ready.
              </p>
              {preflight.draw_created_at && (
                <p className="text-xs text-stone-500">
                  Skating order draw: {new Date(preflight.draw_created_at).toLocaleString()}
                </p>
              )}
            </div>
          ) : (
            <div className="flex items-start gap-2 text-amber-900">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                {!preflight.has_oos && <p>Order of Skate hasn't been generated yet.</p>}
                {preflight.has_oos && !preflight.has_draw && (
                  <p>Run a Skating Order draw before generating the Run Sheet.</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <Button
          className="btn-primary"
          onClick={handleGenerate}
          disabled={!ready || generating}
          data-testid="run-sheet-generate-btn"
        >
          {generating ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Generating…</>
          ) : (
            <><Download className="mr-2 h-4 w-4" />Generate Run Sheet</>
          )}
        </Button>
        {competition && (
          <p className="text-xs text-stone-500 mt-2">
            Will download as <span className="font-mono">Officials_Sheet_{(competition.short_name || competition.name || 'Officials').replace(/[^A-Za-z0-9]/g, '_')}.xlsx</span>
          </p>
        )}
      </div>
    </Card>
  );
}
